import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ajaxconfig } from '../ajaxconfig';
import { BasedUrl } from '../serverurl';

function UserInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [joinedSubscriptions, setJoinedSubscriptions] = useState([]);
  const [activeTab, setActiveTab] = useState('invitations');
  const [isLoading, setIsLoading] = useState(false);
   const navigate = useNavigate();
  const config = ajaxconfig();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch pending invitations
      const invitationsRes = await axios.get(BasedUrl + 'user/subscription/invitations', config);

      console.log(invitationsRes);
      setInvitations(invitationsRes.data.invitations || []);
      
      // Fetch joined subscriptions
      const joinedRes = await axios.get(BasedUrl + 'user/subscription/joined', config);
      setJoinedSubscriptions(joinedRes.data.subscriptions || []);
    } catch (error) {

        console.log(error);
      toast.error('Failed to load subscription data');
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      await axios.post(BasedUrl + `user/subscription/invitation/${invitationId}/accept`, {}, config);
      toast.success('Invitation accepted!');
      fetchData();
    } catch (error) {
        console.log(error);
      toast.error(error.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to reject this invitation?')) return;
    
    setIsLoading(true);
    try {
      await axios.post(BasedUrl + `user/subscription/invitation/${invitationId}/reject`, {}, config);
      toast.success('Invitation rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to leave this subscription? You will lose access to premium features.')) return;
    
    setIsLoading(true);
    try {
      await axios.post(BasedUrl + `user/subscription/leave/${subscriptionId}`, {}, config);
      toast.success('Left subscription successfully');
      fetchData();
    } catch (error) {
        console.log(error);
      toast.error(error.response?.data?.error || 'Failed to leave subscription');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <ToastContainer />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">My Subscription Invitations</h1>
        <p className="text-gray-600 mt-2">Manage subscription invitations you've received</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'invitations' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('invitations')}
        >
          Pending Invitations ({invitations.length})
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'joined' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('joined')}
        >
          Joined Subscriptions ({joinedSubscriptions.length})
        </button>
      </div>

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Pending Invitations</h2>
            {invitations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No pending invitations</p>
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-gray-800">Invitation from {invitation.owner_name}</h3>
                        <p className="text-gray-600">{invitation.owner_email}</p>
                        <div className="mt-2 text-sm text-gray-500">
                          <p>Invited on: {new Date(invitation.invited_at).toLocaleString()}</p>
                          <p>Expires: {new Date(invitation.expires_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAcceptInvitation(invitation.id)}
                          disabled={isLoading}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleRejectInvitation(invitation.id)}
                          disabled={isLoading}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Joined Subscriptions Tab */}
      {activeTab === 'joined' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">My Subscriptions</h2>
            {joinedSubscriptions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active subscriptions</p>
            ) : (
              <div className="space-y-4">
                {joinedSubscriptions.map((subscription) => (
                  <div key={subscription.owner_email} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-gray-800">Subscription with {subscription.owner_name}</h3>
                        <p className="text-gray-600">{subscription.owner_email}</p>
                        <div className="mt-2 text-sm text-gray-500">
                          <p>Joined on: {new Date(subscription.joined_at).toLocaleDateString()}</p>
                          <p>Subscription expires: {new Date(subscription.subscription_expires).toLocaleDateString()}</p>
                          <p className="mt-2 text-blue-600">Status: Active</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleLeaveSubscription(subscription.id)}
                        className="text-red-600 hover:text-red-800 font-semibold"
                      >
                        Leave Subscription
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-800 mb-2">How subscription sharing works:</h3>
        <ul className="list-disc list-inside text-blue-700 space-y-1">
          <li>Accept invitations to join someone else's subscription plan</li>
          <li>You'll get access to premium features while the subscription is active</li>
          <li>You can leave a subscription anytime</li>
          <li>If the owner cancels their subscription, all shared users lose access</li>
          <li>You can only be part of one subscription at a time</li>
        </ul>
      </div>


      <div className="mt-8">
  <div className="flex flex-col sm:flex-row gap-4">
    {/* WhatsApp Button - Full width on mobile, auto on larger screens */}
    <a
      href="https://wa.me/2348113315252"
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
    >
      <i className="fab fa-whatsapp text-lg sm:text-xl"></i>
      <span className="text-sm sm:text-base md:text-lg lg:text-base">
        Chat on WhatsApp
      </span>
    </a>

    {/* Back to Payment Button - Full width on mobile, auto on larger screens */}
    <button
      onClick={() => navigate('/cbt_creator/payment')}
      className="group flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-colors duration-300 w-full sm:w-auto sm:flex-1"
    >
      <i className="fas fa-arrow-left text-lg sm:text-xl"></i>
      <span className="text-sm sm:text-base md:text-lg lg:text-base">
        Back to Payment
      </span>
    </button>
  </div>
</div>
{/* end */}



    </div>
  );
}

export default UserInvitations;